/**
 * ตัวแดง / ตัวเหลือง (duel armour, lane DUEL-ARMOR; design docs/design/combat/2026-10-11-duel-armor.md, research
 * docs/research/DUEL_ARMOR.md). Armour is a short window on a move that filters the blow's REACTION, never its damage:
 * - red: nothing moves it: no stun, no interrupt, no shove, no fling, no knock-down; a lock form on it misses;
 * - yellow: no shove, no fling, no knock-down (a fling or trip becomes a short stun in place), but a stun lands (and ends the
 *   move and the armour); a lock form grabs it; each blow it soaks costs พลังกาย, and when that runs short it breaks.
 * Never on a break, never in a recovery. Data only.
 */

import { STATUSES } from '../status';

export type ArmorTier = 'red' | 'yellow';

/** The part of a move a window covers: its wind-up (`Entity.cast`) or its spin (`Entity.spinT`). */
export type ArmorPhase = 'windup' | 'spin';

export interface ArmorWindow {
  readonly tier: ArmorTier;
  readonly phase: ArmorPhase;
}

export const DUEL_ARMOR = {
  /** The longest a window lasts (s), whatever the phase: armour stays a moment, not a state. */
  redMax: 0.7,
  yellowMax: 1.2,
  /** พลังกาย a yellow body pays for each blow it soaks; short of it, the armour breaks (that blow lands in full). */
  yellowCost: 12,
  /** A fling or a trip on a yellow body: this stun in place instead (s). */
  yellowStun: 0.35,
  /** A broken yellow armour: this stagger (s). */
  breakStagger: 0.3,
  /** A landed break, or a pierce move (`ARMOR_PIERCE`), on a yellow body: this stun (s). */
  pierceStun: 0.5,
} as const;

/** Moves whose stun answers yellow armour even without a stun of their own (the armour answer of an art). */
export const ARMOR_PIERCE: readonly string[] = ['jz-seal'];

const RED_WINDUP: readonly ArmorWindow[] = [{ tier: 'red', phase: 'windup' }];
const YELLOW_WINDUP: readonly ArmorWindow[] = [{ tier: 'yellow', phase: 'windup' }];

/**
 * The armoured moves (skill id -> windows). Red: the ultimates' wind-ups (a full rage bar, about once a round) and the
 * signature heavy move of each hard-to-get art. Yellow: one or two heavy moves of the duel arts. A move with no wind-up in
 * the bout has no window to armour (the bout gives dashes, circles and heavy blows a wind-up: data/duelMatch.ts bigMove).
 */
export const ARMOR_MOVES: Readonly<Record<string, readonly ArmorWindow[]>> = {
  // --- red: ultimates (wind-up only) ---
  'bs-tempest': RED_WINDUP,
  'ar-buddha': RED_WINDUP,
  'jz-scroll': RED_WINDUP,
  'bs-massacre': [
    { tier: 'red', phase: 'windup' },
    { tier: 'yellow', phase: 'spin' },
  ],
  'cp-heaven': RED_WINDUP,
  'tf-supreme': RED_WINDUP,
  'ts-heaven': RED_WINDUP,
  'ss-hatred': RED_WINDUP,
  'jy-dragon': RED_WINDUP,
  'jl-abyss': RED_WINDUP,
  'pp-tathagata': RED_WINDUP,
  'fm-vanquish': RED_WINDUP,
  'ah-imperial': RED_WINDUP,
  'sp-decree': RED_WINDUP,
  'ms-eclipse': RED_WINDUP,
  'dd-heaven': RED_WINDUP,
  'sf-divine': RED_WINDUP,
  'sg-masterpiece': RED_WINDUP,
  'bc-tiger': RED_WINDUP,
  'rc-sea': [
    { tier: 'red', phase: 'windup' },
    { tier: 'yellow', phase: 'spin' },
  ],
  'dp-eighteen': RED_WINDUP,
  'hw-stars': RED_WINDUP,
  'dp9-sky': RED_WINDUP,
  'ns-heaven': RED_WINDUP,
  // --- red: the signature heavy move of a hard-to-get art (top of a school, a special or legendary art) ---
  'ts-thread': RED_WINDUP, // กระบี่วงธารา
  'fm-stride': RED_WINDUP, // ดาบค้อนช่างใหญ่
  'sp-hunt': RED_WINDUP, // ดาบชักเร็วโคมเหล็ก
  'dd-drain': RED_WINDUP, // เพลงหมู่เงาจันทร์
  'sg-stroke': RED_WINDUP, // ขีดเดียวไร้ถ้อย
  'rc-hunt': RED_WINDUP, // ดาบผลักผาชาด
  'dp-leap': RED_WINDUP, // ฝ่ามือมังกรพายุ
  'ss-trace': RED_WINDUP, // กระบี่ธารเยือกแข็ง (a dash again: the lock moved to ss-shift, kit audit 10-13)
  'dp9-whirl': RED_WINDUP, // ฝ่ามือมังกรทองเก้าสวรรค์
  'ns-thunder': RED_WINDUP, // เพลงกระบี่ร่องศิลา
  // --- yellow: the duel arts' heavy moves ---
  'bs-glide': YELLOW_WINDUP, // กระบี่ไผ่ห่มหมอก: the passing cut
  'bs-charge': YELLOW_WINDUP, // ดาบชาดคลั่งไฟ: the charge
  'ar-step': YELLOW_WINDUP, // หมัดระฆังก้อง: the iron step in
  'ar-sweep': YELLOW_WINDUP, // หมัดระฆังก้อง: the low sweep
};

/** A move's windows (none: []). */
export function armorWindowsOf(skillId: string): readonly ArmorWindow[] {
  return ARMOR_MOVES[skillId] ?? [];
}

/**
 * A control status (it takes the fighter's actions away: a stun, a seal, a silence, a charm or trance, a root off the
 * qinggong, a trap that stuns): red armour does not take these from a blow's `applies` (World.applyHitNow). Damage over
 * time and plain slows still land.
 */
export function isControlStatus(id: string): boolean {
  const d = STATUSES[id] as
    | {
        drCategory?: string;
        stunAtMax?: number;
        noSkills?: boolean;
        noQinggong?: boolean;
        trapStun?: unknown;
      }
    | undefined;
  if (!d) return false;
  return (
    d.drCategory === 'stun' ||
    d.drCategory === 'silence' ||
    !!d.stunAtMax ||
    !!d.noSkills ||
    !!d.noQinggong ||
    !!d.trapStun ||
    id === 'charm' ||
    id === 'trance'
  );
}
